import { useToken } from '@chakra-ui/react';
import * as React from 'react';

export interface UsersProps extends React.SVGProps<SVGSVGElement> {
  size?: number | string; // Can be number (pixels) or theme token string (e.g., "md", "24px")
  variant?: 'outline' | 'filled' | 'gradient';
  color?: string;
  fillColor?: string;
  strokeColor?: string;
  gradientFrom?: string;
  gradientTo?: string;
  colorPalette?: string;
}

export const Users = React.memo(
  React.forwardRef<SVGSVGElement, UsersProps>(function Users(
    {
      size = 24,
      variant = 'outline',
      color = 'gray.500', // Default outline color
      fillColor,
      strokeColor,
      gradientFrom, // Default gradient start
      gradientTo, // Default gradient end
      colorPalette, // Optional color scheme override
      width,
      height,
      ...props
    },
    ref
  ) {
    // If colorPalette is provided, use it as the base for defaults
    const baseColor = colorPalette ? `${colorPalette}.500` : color;
    const defaultGradFrom = colorPalette ? `${colorPalette}.400` : 'primary.500';
    const defaultGradTo = colorPalette ? `${colorPalette}.600` : 'blue.400';

    // Determine if size is a token or a direct number
    const isTokenSize = typeof size === 'string' && !/\d+(px)?$/.test(size);

    // Resolve tokens
    const [
      tokenColor,
      tokenFill,
      tokenStroke,
      tokenGradFrom,
      tokenGradTo,
      tokenOutlineStroke, // Specific color for the outline paths
      resolvedSize, // Resolve size token if needed
    ] = useToken('colors', [
      baseColor,
      fillColor ?? baseColor, // Use explicit fillColor or derived baseColor
      strokeColor ?? baseColor, // Use explicit strokeColor or derived baseColor
      gradientFrom ?? defaultGradFrom,
      gradientTo ?? defaultGradTo,
      '#2D264B', // Keep the original outline color for now, can be prop later if needed
      isTokenSize ? size : '', // Pass empty string if not resolving size token
    ]);

    // Stable gradient ID
    const gradIdBase = React.useId();
    const gradIdCenter = `${gradIdBase}-center`;
    const gradIdBody = `${gradIdBase}-body`;

    // Fill logic for center face/body based on variant and props
    const centerFill =
      fillColor != null
        ? tokenFill
        : variant === 'gradient'
          ? `url(#${gradIdCenter})`
          : variant === 'filled'
            ? tokenFill
            : 'none';
    const bodyFill =
      fillColor != null
        ? tokenFill // Use same fill color if provided explicitly
        : variant === 'gradient'
          ? `url(#${gradIdBody})`
          : variant === 'filled'
            ? tokenFill
            : 'none';

    // Determine stroke for the main filled/gradient parts (not the outline paths)
    const mainStroke =
      strokeColor != null ? tokenStroke : variant === 'outline' ? tokenColor : 'none';
    const strokeWidth = variant === 'outline' ? 1.5 : 0; // Only apply stroke in outline mode

    // Use resolvedSize if size was a token, otherwise use size directly (could be number or string like "24px")
    const finalSize = isTokenSize ? resolvedSize : size;
    const finalWidth = width ?? finalSize;
    const finalHeight = height ?? finalSize;

    // Opacity for gradient variant (consistent with original logic, could be removed if not desired)
    const fillOpacity = variant === 'gradient' && fillColor == null ? 0.5 : 1;

    return (
      <svg
        ref={ref}
        width={finalWidth}
        height={finalHeight}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        {...props}
      >
        {/* Center face */}
        <path
          d="M15 7.75C15 9.40685 13.6569 10.75 12 10.75C10.3431 10.75 9 9.40685 9 7.75C9 6.09315 10.3431 4.75 12 4.75C13.6569 4.75 15 6.09315 15 7.75Z"
          fill={centerFill}
          fillOpacity={fillOpacity}
          stroke={mainStroke}
          strokeWidth={strokeWidth}
        />
        {/* Center body */}
        <path
          d="M6 16.75C6 15.0931 7.34315 13.75 9 13.75H15C16.6569 13.75 18 15.0931 18 16.75C18 18.4069 16.6569 19.75 15 19.75H9C7.34315 19.75 6 18.4069 6 16.75Z"
          fill={bodyFill}
          fillOpacity={fillOpacity}
          stroke={mainStroke}
          strokeWidth={strokeWidth}
        />
        {/* Outline and other faces/bodies - always use the specific outline stroke color */}
        <path
          d="M14.25 7.75C14.25 8.99264 13.2426 10 12 10V11.5C14.0711 11.5 15.75 9.82107 15.75 7.75H14.25ZM12 10C10.7574 10 9.75 8.99264 9.75 7.75H8.25C8.25 9.82107 9.92893 11.5 12 11.5V10ZM9.75 7.75C9.75 6.50736 10.7574 5.5 12 5.5V4C9.92893 4 8.25 5.67893 8.25 7.75H9.75ZM12 5.5C13.2426 5.5 14.25 6.50736 14.25 7.75H15.75C15.75 5.67893 14.0711 4 12 4V5.5ZM9 14.5H15V13H9V14.5ZM15 19H9V20.5H15V19ZM9 19C7.75736 19 6.75 17.9926 6.75 16.75H5.25C5.25 18.8211 6.92893 20.5 9 20.5V19ZM17.25 16.75C17.25 17.9926 16.2426 19 15 19V20.5C17.0711 20.5 18.75 18.8211 18.75 16.75H17.25ZM15 14.5C16.2426 14.5 17.25 15.5074 17.25 16.75H18.75C18.75 14.6789 17.0711 13 15 13V14.5ZM9 13C6.92893 13 5.25 14.6789 5.25 16.75H6.75C6.75 15.5074 7.75736 14.5 9 14.5V13Z"
          fill={tokenOutlineStroke} // Use resolved token for outline paths
        />
        <path
          d="M7.75214 10.3887C7.59441 10.1353 7.29846 10 7 10C5.75736 10 4.75 8.99264 4.75 7.75C4.75 6.50736 5.75736 5.5 7 5.5C7.29846 5.5 7.59441 5.36473 7.75214 5.11135C7.75912 5.10014 7.76613 5.08896 7.7732 5.07782C8.0358 4.66331 7.90275 4.0764 7.415 4.0227C7.27873 4.0077 7.14027 4 7 4C4.92893 4 3.25 5.67893 3.25 7.75C3.25 9.82107 4.92893 11.5 7 11.5C7.14027 11.5 7.27873 11.4923 7.415 11.4773C7.90275 11.4236 8.0358 10.8367 7.7732 10.4222C7.76614 10.411 7.75912 10.3999 7.75214 10.3887Z"
          fill={tokenOutlineStroke} // Use resolved token for outline paths
        />
        <path
          d="M4.70829 18.3169C4.59477 18.1275 4.39439 18 4.17359 18H4C2.75736 18 1.75 16.9926 1.75 15.75C1.75 14.5074 2.75736 13.5 4 13.5H4.17359C4.39439 13.5 4.59477 13.3725 4.70829 13.1831C4.98539 12.7208 4.68468 12 4.14569 12H4C1.92893 12 0.25 13.6789 0.25 15.75C0.25 17.8211 1.92893 19.5 4 19.5H4.14569C4.68469 19.5 4.98539 18.7792 4.70829 18.3169Z"
          fill={tokenOutlineStroke} // Use resolved token for outline paths
        />
        <path
          d="M16.2268 10.4222C15.9642 10.8367 16.0973 11.4236 16.585 11.4773C16.7213 11.4923 16.8597 11.5 17 11.5C19.0711 11.5 20.75 9.82107 20.75 7.75C20.75 5.67893 19.0711 4 17 4C16.8597 4 16.7213 4.0077 16.585 4.0227C16.0973 4.0764 15.9642 4.66331 16.2268 5.07782C16.2339 5.08896 16.2409 5.10014 16.2479 5.11134C16.4056 5.36472 16.7015 5.5 17 5.5C18.2426 5.5 19.25 6.50736 19.25 7.75C19.25 8.99264 18.2426 10 17 10C16.7015 10 16.4056 10.1353 16.2479 10.3887C16.2409 10.3999 16.2339 10.411 16.2268 10.4222Z"
          fill={tokenOutlineStroke} // Use resolved token for outline paths
        />
        <path
          d="M19.2917 18.3169C19.0146 18.7792 19.3153 19.5 19.8543 19.5H20C22.0711 19.5 23.75 17.8211 23.75 15.75C23.75 13.6789 22.0711 12 20 12H19.8543C19.3153 12 19.0146 12.7208 19.2917 13.1831C19.4052 13.3725 19.6056 13.5 19.8264 13.5H20C21.2426 13.5 22.25 14.5074 22.25 15.75C22.25 16.9926 21.2426 18 20 18H19.8264C19.6056 18 19.4052 18.1275 19.2917 18.3169Z"
          fill={tokenOutlineStroke} // Use resolved token for outline paths
        />
        {variant === 'gradient' && fillColor == null && (
          <defs>
            <linearGradient
              id={gradIdCenter} // Use stable ID
              x1="12" // Example gradient coordinates, adjust as needed
              y1="4"
              x2="12"
              y2="11"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor={tokenGradFrom} />
              <stop offset="1" stopColor={tokenGradTo} />
            </linearGradient>
            <linearGradient
              id={gradIdBody} // Use stable ID
              x1="12" // Example gradient coordinates, adjust as needed
              y1="13"
              x2="12"
              y2="20"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor={tokenGradFrom} />
              <stop offset="1" stopColor={tokenGradTo} />
            </linearGradient>
          </defs>
        )}
      </svg>
    );
  })
);

Users.displayName = 'Users';

export default Users;
